// src/core/libs/i18n/i18n.ts
import i18n, { type InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";

// Namespaces and languages you actually have:
const ns = ["common", "auth", "sales", "workspace", "project", "documents"] as const;
const supportedLngs = ["fr", "en"] as const;
// keep your preferred fallback
const fallbackLng = "fr";

// Shared config
const baseConfig: InitOptions = {
  lng: "fr",
  fallbackLng: "fr",
  supportedLngs: [...supportedLngs],
  ns: [...ns],
  defaultNS: "common",
  fallbackNS: "common",
  interpolation: { escapeValue: false },
  // Normalize "en-US" -> "en", "fr-FR" -> "fr"
  nonExplicitSupportedLngs: true,
  load: "languageOnly",
  // Don’t return null/empty if key missing (prevents weird blanks)
  returnNull: false,
  returnEmptyString: false,
  detection: {
    // LocalStorage first so your chosen language sticks
    order: ["localStorage", "navigator", "htmlTag", "path", "subdomain"],
    caches: ["localStorage"],
    lookupLocalStorage: "i18nextLng",
  },
  // Turn on in dev if you want extra logs:
  // debug: import.meta.env.MODE !== "production",
};

// Bundle JSON from src via Vite’s glob and hand it to i18next
const devAndProdBackend = resourcesToBackend(
  (lng: string, namespace: string) => {
    // Adjust the glob if your folder ever moves
    const modules = import.meta.glob(
      "../../locales/*/*.json",
      { eager: true }, // eager -> modules[key].default is immediately available
    ) as Record<string, { default: unknown }>;

    // First try the exact match
    const key = `../../locales/${lng}/${namespace}.json`;
    if (modules[key]) return Promise.resolve(modules[key].default);

    // If that file doesn’t exist, try fallback language
    const fbKey = `../../locales/${fallbackLng}/${namespace}.json`;
    if (modules[fbKey]) return Promise.resolve(modules[fbKey].default);

    // Last resort: empty object (prevents hard crashes during authoring)
    return Promise.resolve({});
  },
);

const initI18n = async () => {
  await i18n
    .use(devAndProdBackend) // single backend for both envs
    .use(LanguageDetector)
    .use(initReactI18next)
    .init(baseConfig);
};

// Kick off init; main.tsx will wait for "initialized" before first render
initI18n();

export default i18n;
